(() => {
  if (!customElements.get('redd-quantity')) customElements.define('redd-quantity', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[data-quantity-minus]');
      this.plus = this.querySelector('[data-quantity-plus]');
      if (!this.input || !this.minus || !this.plus) return;
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      this.sync = () => {
        const value = this.input.valueAsNumber;
        const min = Number(this.input.min) || 1;
        const step = Number(this.input.step) || 1;
        const max = this.input.max === '' ? Infinity : Number(this.input.max);
        this.minus.disabled = this.input.disabled || (Number.isFinite(value) && value <= min);
        const highest = min + Math.floor((max - min) / step) * step;
        this.plus.disabled = this.input.disabled || (Number.isFinite(value) && value >= highest);
      };
      const adjust = (direction) => {
        if (!Number.isFinite(this.input.valueAsNumber)) this.input.value = this.input.min || '1';
        else if (direction > 0) this.input.stepUp();
        else this.input.stepDown();
        this.input.dispatchEvent(new Event('input', {bubbles: true}));
        this.input.dispatchEvent(new Event('change', {bubbles: true}));
      };
      this.minus.addEventListener('click', () => adjust(-1), options);
      this.plus.addEventListener('click', () => adjust(1), options);
      this.input.addEventListener('input', this.sync, options);
      this.input.addEventListener('change', this.sync, options);
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.input, {attributes: true, attributeFilter: ['min', 'max', 'step', 'disabled']});
      this.minus.hidden = false;
      this.plus.hidden = false;
      this.sync();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
    }
  });
  if (!customElements.get('redd-announcement')) customElements.define('redd-announcement', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.redd-announcement__track');
      this.list = this.track?.querySelector('ul');
      if (!this.list) return;
      // A second, silent copy makes the mobile loop seamless for every language.
      this.copy = this.list.cloneNode(true);
      this.copy.setAttribute('aria-hidden', 'true');
      this.copy.removeAttribute('aria-label');
      this.track.append(this.copy);
      this.classList.add('is-enhanced');
      this.header = this.closest('#header-group');
      this.observer = new ResizeObserver(() => {
        // Preserve fractional widths and avoid retiming the loop on header-only resizes.
        const width = this.list.getBoundingClientRect().width;
        if (width > 0 && (!this.width || Math.abs(width - this.width) > 0.1)) {
          this.width = width;
          this.style.setProperty('--redd-marquee-duration', `${width / 28}s`);
        }
        if (this.header) document.documentElement.style.setProperty('--redd-header-height', `${this.header.offsetHeight}px`);
      });
      this.observer.observe(this.list);
      if (this.header) this.observer.observe(this.header);
      this.events = new AbortController();
      const visibility = () => this.classList.toggle('is-paused', document.hidden);
      document.addEventListener('visibilitychange', visibility, {signal: this.events.signal});
      visibility();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.copy?.remove();
      this.width = null;
      this.classList.remove('is-enhanced', 'is-paused');
    }
  });
  if (!customElements.get('redd-voice')) customElements.define('redd-voice', class extends HTMLElement {
    connectedCallback() {
      this.commands = Array.from(this.querySelectorAll('[data-voice-command]'));
      this.prompt = this.querySelector('.redd-voice__hello');
      this.controls = this.querySelector('.redd-voice__controls');
      this.count = this.querySelector('[data-voice-count]');
      this.status = this.querySelector('[data-voice-status]');
      this.index = 0;
      this.hovered = this.focused = this.visible = this.promptVisible = false;
      if (this.status) this.status.textContent = '';
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      this.querySelector('[data-voice-previous]')?.addEventListener('click', () => this.showCommand(this.index - 1, true), options);
      this.querySelector('[data-voice-next]')?.addEventListener('click', () => this.showCommand(this.index + 1, true), options);
      this.prompt?.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.syncPlayback();
      }, options);
      this.prompt?.addEventListener('pointerleave', () => { this.hovered = false; this.syncPlayback(); }, options);
      this.prompt?.addEventListener('focusin', () => { this.focused = true; this.syncPlayback(); }, options);
      this.prompt?.addEventListener('focusout', (event) => {
        this.focused = this.prompt.contains(event.relatedTarget);
        this.syncPlayback();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      if (this.controls) this.controls.hidden = this.commands.length < 2;
      this.showCommand(0);
      // Start examples only when the prompt itself is in view, not while the
      // preceding copy fills the phone screen. No microphone or audio is used.
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === this) this.visible = entry.isIntersecting;
          if (entry.target === this.prompt) this.promptVisible = entry.isIntersecting && entry.intersectionRatio >= .5;
        });
        this.syncPlayback();
      }, {threshold: [0, .5]});
      this.observer.observe(this);
      if (this.prompt) this.observer.observe(this.prompt);
    }
    showCommand(index, announce = false) {
      if (!this.commands.length) return;
      this.index = (index + this.commands.length) % this.commands.length;
      this.commands.forEach((command, i) => {
        command.classList.toggle('is-active', i === this.index);
        command.setAttribute('aria-hidden', String(i !== this.index));
      });
      this.count.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.commands.length).padStart(2, '0')}`;
      // Only manual changes are announced; autoplay must not interrupt reading.
      if (announce) this.status.textContent = this.commands[this.index].textContent.trim();
      this.syncPlayback();
    }
    syncPlayback() {
      clearTimeout(this.timer);
      this.timer = null;
      this.classList.toggle('is-playing', this.visible && !document.hidden);
      if (!this.promptVisible || document.hidden || this.hovered || this.focused || this.commands.length < 2) return;
      this.timer = setTimeout(() => this.showCommand(this.index + 1), 3000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.timer = null;
      this.events?.abort();
      this.observer?.disconnect();
      this.classList.remove('is-playing');
    }
  });
  if (!customElements.get('redd-product-navigation')) customElements.define('redd-product-navigation', class extends HTMLElement {
    connectedCallback() {
      this.entries = Array.from(this.querySelectorAll('a[href^="#"]')).map((link) => ({
        link,
        section: document.getElementById(link.hash.slice(1)),
      })).filter(({section}) => section);
      if (!this.entries.length) return;
      this.nav = this.closest('.redd-product-jump');
      this.events = new AbortController();
      this.queue = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateActive();
        });
      };
      window.addEventListener('scroll', this.queue, {passive: true, signal: this.events.signal});
      window.addEventListener('resize', this.queue, {passive: true, signal: this.events.signal});
      this.observer = new ResizeObserver(() => {
        // Anchor links must clear both the header and this sticky menu.
        this.entries.forEach(({section}) => section.style.setProperty('scroll-margin-top', `${this.nav.offsetHeight}px`));
        this.queue();
      });
      [this.nav, document.getElementById('MainContent'), document.getElementById('header-group')].filter(Boolean).forEach((element) => this.observer.observe(element));
      this.queue();
    }
    updateActive() {
      const boundary = (parseFloat(getComputedStyle(this.nav).top) || 0) + this.nav.offsetHeight + 24;
      let active = this.entries[0].link;
      for (const {link, section} of this.entries) {
        if (section.getBoundingClientRect().top <= boundary) active = link;
      }
      const resized = this.width !== this.clientWidth;
      this.width = this.clientWidth;
      if (active === this.active && !resized) return;
      this.active = active;
      this.entries.forEach(({link}) => {
        if (link === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      // Move only the horizontal menu, never the document or keyboard focus.
      const menu = this.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      if (item.left < menu.left + 12 || item.right > menu.right - 12) {
        this.scrollTo({left: this.scrollLeft + item.left - menu.left - (this.clientWidth - item.width) / 2, behavior: 'smooth'});
      }
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.active = null;
      this.entries?.forEach(({section}) => section.style.removeProperty('scroll-margin-top'));
    }
  });
  const initialise = (root = document) => {
    // Choose the description's initial state once; leave later customer toggles alone.
    root.querySelectorAll('[data-redd-product-description]').forEach((details) => {
      if (details.dataset.descriptionReady) return;
      details.dataset.descriptionReady = 'true';
      details.open = window.matchMedia('(min-width: 750px)').matches;
    });
    root.querySelectorAll('[data-redd-intensity]').forEach((section) => {
      if (section.dataset.ready) return;
      const visual = section.querySelector('.redd-intensity__visual');
      const levels = Array.from(section.querySelectorAll('.redd-intensity__choices input[type="radio"]'));
      if (!visual || levels.length < 2) return;
      section.dataset.ready = 'true';
      let gesture;
      visual.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        gesture = {id: event.pointerId, x: event.clientX, y: event.clientY};
        visual.setPointerCapture(event.pointerId);
      });
      visual.addEventListener('pointerup', (event) => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        gesture = null;
        // Horizontal swipes cycle levels; vertical scrolling and taps stay native.
        if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
        const current = levels.findIndex((level) => level.checked);
        const next = levels[(current + (dx < 0 ? 1 : -1) + levels.length) % levels.length];
        next.checked = true;
        next.dispatchEvent(new Event('input', {bubbles: true}));
        next.dispatchEvent(new Event('change', {bubbles: true}));
      });
      const cancelGesture = () => { gesture = null; };
      visual.addEventListener('pointercancel', cancelGesture);
      visual.addEventListener('lostpointercapture', cancelGesture);
    });
    root.querySelectorAll('[data-redd-localization]').forEach((chooser) => {
      if (chooser.dataset.ready) return;
      chooser.dataset.ready = 'true';
      const form = chooser.querySelector('form');
      form.addEventListener('submit', () => {
        const destination = new URL(location.href);
        for (const key of ['country', 'country_code', 'currency', 'language', 'language_code', 'locale', 'lang', 'return_to']) destination.searchParams.delete(key);
        const returnTo = form.elements.namedItem('return_to');
        if (returnTo) returnTo.value = destination.pathname + destination.search + destination.hash;
      });
      chooser.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        chooser.open = false;
        chooser.querySelector('summary').focus();
      });
    });
    // Merchant-authored rich text can also contain the brand. Preserve its text
    // alternatives while using the same supplied artwork as the Liquid copy.
    const brandSource = document.querySelector('.redd-logo')?.getAttribute('src');
    if (brandSource) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          return /\bREDD\b/i.test(node.nodeValue) && !node.parentElement?.closest('head,title,script,style,textarea,option,svg,template,.redd-wordmark,[data-redd-plain-text],[contenteditable],.visually-hidden') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        },
      });
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const pieces = node.nodeValue.split(/\bREDD\b/i);
        const fragment = document.createDocumentFragment();
        pieces.forEach((piece, index) => {
          if (index) {
            const mark = document.createElement('span');
            mark.className = 'redd-wordmark';
            const image = document.createElement('img');
            image.src = brandSource;
            image.alt = 'REDD';
            image.width = 5000;
            image.height = 1560;
            mark.append(image);
            fragment.append(mark);
          }
          fragment.append(document.createTextNode(piece));
        });
        node.replaceWith(fragment);
      });
    }
    root.querySelectorAll('[data-redd-gallery]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-redd-gallery-track]');
      const slides = Array.from(track.children);
      const range = gallery.querySelector('[data-gallery-range]');
      const caption = gallery.querySelector('[data-gallery-caption]');
      const count = gallery.querySelector('[data-gallery-count]');
      const previous = gallery.querySelector('[data-gallery-prev]');
      const next = gallery.querySelector('[data-gallery-next]');
      gallery.querySelector('[data-redd-gallery-controls]').hidden = false;
      gallery.classList.add('is-enhanced');
      range.max = String(slides.length - 1);
      let index = 0;
      let drag = null;
      const update = () => {
        index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        range.value = index;
        range.setAttribute('aria-valuetext', slides[index].dataset.galleryLabel);
        caption.textContent = slides[index].dataset.galleryLabel;
        count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
        previous.disabled = index === 0;
        next.disabled = index === slides.length - 1;
      };
      const go = (target) => track.scrollTo({left: Math.max(0, Math.min(slides.length - 1, target)) * track.clientWidth, behavior: 'smooth'});
      track.addEventListener('scroll', update, {passive: true});
      previous.addEventListener('click', () => go(index - 1));
      next.addEventListener('click', () => go(index + 1));
      range.addEventListener('input', () => go(Number(range.value)));
      track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        go(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      // Touch keeps native momentum scrolling. Mouse dragging snaps to the nearest view.
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = {x: event.clientX, left: track.scrollLeft};
        track.setPointerCapture(event.pointerId);
        track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', (event) => {
        if (drag) track.scrollLeft = drag.left + drag.x - event.clientX;
      });
      const finishDrag = () => {
        if (!drag) return;
        drag = null;
        track.classList.remove('is-dragging');
        go(Math.round(track.scrollLeft / track.clientWidth));
      };
      track.addEventListener('pointerup', finishDrag);
      track.addEventListener('pointercancel', finishDrag);
      track.addEventListener('lostpointercapture', finishDrag);
      if ('ResizeObserver' in window) new ResizeObserver(() => {track.scrollTo({left: index * track.clientWidth, behavior: 'instant'});}).observe(track);
      update();
    });
    root.querySelectorAll('[data-redd-media]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-media-track]');
      const slides = Array.from(track.querySelectorAll('[data-media-slide]'));
      if (!slides.length) return;
      const thumbs = gallery.querySelector('[data-media-thumbs]');
      const previous = gallery.querySelector('[data-media-prev]');
      const next = gallery.querySelector('[data-media-next]');
      const dialog = gallery.querySelector('[data-media-dialog]');
      let index = 0;
      const go = (target) => track.scrollTo({
        left: Math.max(0, Math.min(slides.length - 1, target)) * track.clientWidth,
        behavior: 'smooth',
      });
      const buttons = slides.map((slide, number) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', slide.dataset.label);
        button.setAttribute('aria-controls', track.id);
        const image = document.createElement('img');
        image.src = slide.querySelector('img').src;
        image.className = slide.querySelector('img').className;
        image.alt = '';
        image.width = 80;
        image.height = 80;
        button.append(image);
        button.addEventListener('click', () => go(number));
        thumbs.append(button);
        return button;
      });
      const update = () => {
        index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        gallery.querySelector('[data-media-caption]').textContent = slides[index].dataset.label;
        gallery.querySelector('[data-media-count]').textContent = `${index + 1} / ${slides.length}`;
        previous.disabled = index === 0;
        next.disabled = index === slides.length - 1;
        buttons.forEach((button, i) => {
          button.setAttribute('aria-pressed', String(i === index));
          slides[i].querySelector('a').tabIndex = i === index ? 0 : -1;
        });
      };
      track.addEventListener('scroll', update, {passive: true});
      track.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        go(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      previous.addEventListener('click', () => go(index - 1));
      next.addEventListener('click', () => go(index + 1));
      gallery.querySelector('[data-media-navigation]').hidden = slides.length < 2;
      thumbs.hidden = slides.length < 2;
      if (typeof dialog.showModal === 'function') {
        const zoomTrack = dialog.querySelector('[data-media-lightbox-track]');
        const zoomPrevious = dialog.querySelector('[data-media-lightbox-prev]');
        const zoomNext = dialog.querySelector('[data-media-lightbox-next]');
        const zoomButton = gallery.querySelector('[data-media-zoom]');
        let zoomSlides = [];
        let zoomIndex = 0;
        let zoomOpener;
        let zoomFrame;
        const updateZoom = () => {
          dialog.querySelector('[data-media-lightbox-caption]').textContent = slides[zoomIndex].dataset.label;
          dialog.querySelector('[data-media-lightbox-count]').textContent = `${zoomIndex + 1} / ${slides.length}`;
          zoomPrevious.disabled = zoomIndex === 0;
          zoomNext.disabled = zoomIndex === slides.length - 1;
          zoomSlides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== zoomIndex)));
        };
        const goZoom = (target, instant = false) => {
          zoomIndex = Math.max(0, Math.min(slides.length - 1, target));
          zoomSlides[zoomIndex].querySelector('img').loading = 'eager';
          zoomTrack.scrollTo({
            left: zoomIndex * zoomTrack.clientWidth,
            behavior: instant ? 'instant' : 'smooth',
          });
          updateZoom();
        };
        const zoom = (target, opener) => {
          if (!zoomSlides.length) {
            zoomSlides = slides.map(() => {
              const figure = document.createElement('figure');
              const image = document.createElement('img');
              image.loading = 'lazy';
              image.draggable = false;
              figure.append(image);
              zoomTrack.append(figure);
              return figure;
            });
          }
          // Read fresh sources so a newly selected colour opens its current hero image.
          slides.forEach((slide, i) => {
            const source = slide.querySelector('img');
            const image = zoomSlides[i].querySelector('img');
            image.src = slide.querySelector('a').href;
            image.alt = source.alt;
          });
          zoomOpener = opener;
          dialog.showModal();
          document.documentElement.classList.add('redd-media-zoom-open');
          goZoom(target, true);
        };
        zoomPrevious.hidden = zoomNext.hidden = slides.length < 2;
        zoomPrevious.addEventListener('click', () => goZoom(zoomIndex - 1));
        zoomNext.addEventListener('click', () => goZoom(zoomIndex + 1));
        zoomTrack.addEventListener('scroll', () => {
          if (zoomFrame) return;
          zoomFrame = requestAnimationFrame(() => {
            zoomFrame = null;
            if (!dialog.open || !zoomTrack.clientWidth) return;
            zoomIndex = Math.max(0, Math.min(slides.length - 1, Math.round(zoomTrack.scrollLeft / zoomTrack.clientWidth)));
            updateZoom();
          });
        }, {passive: true});
        dialog.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          goZoom(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : zoomIndex + (event.key === 'ArrowRight' ? 1 : -1));
        });
        zoomButton.hidden = false;
        zoomButton.addEventListener('click', () => zoom(index, zoomButton));
        gallery.querySelectorAll('[data-media-open]').forEach((link, number) => link.addEventListener('click', (event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          zoom(number, link);
        }));
        dialog.querySelector('[data-media-close]').addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
        dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('redd-media-zoom-open');
          track.scrollTo({left: zoomIndex * track.clientWidth, behavior: 'instant'});
          update();
          const returnFocus = zoomOpener === zoomButton ? zoomButton : slides[zoomIndex].querySelector('a');
          returnFocus.focus({preventScroll: true});
        });
        if ('ResizeObserver' in window) new ResizeObserver(() => {
          if (dialog.open) goZoom(zoomIndex, true);
        }).observe(zoomTrack);
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.target.contains(gallery) && dialog.open) dialog.close();
        });
      }
      gallery.addEventListener('redd:media-variant', () => {
        const heroIndex = slides.findIndex((slide) => slide.querySelector('[data-redd-product-hero]'));
        if (heroIndex < 0) return;
        const hero = slides[heroIndex].querySelector('img');
        slides[heroIndex].querySelector('a').href = hero.src;
        buttons[heroIndex].querySelector('img').src = hero.src;
        track.scrollTo({left: heroIndex * track.clientWidth, behavior: 'instant'});
        update();
      });
      if ('ResizeObserver' in window) new ResizeObserver(() => track.scrollTo({left: index * track.clientWidth, behavior: 'instant'})).observe(track);
      update();
    });
    root.querySelectorAll('[data-redd-purchase-dock]').forEach((dock) => {
      if (dock.dataset.ready) return;
      const form = document.getElementById(dock.dataset.formId);
      if (!form) return;
      dock.dataset.ready = 'true';
      const mainButton = form.querySelector('[type="submit"]');
      const button = dock.querySelector('[data-dock-submit]');
      const price = form.closest('.redd-product__details')?.querySelector('[data-redd-price]');
      const closingButtons = (dock.closest('.shopify-section') || root).querySelectorAll('.redd-finale [type="submit"]');
      const sync = () => {
        button.disabled = mainButton.disabled;
        button.textContent = mainButton.textContent;
        button.setAttribute('aria-busy', String(mainButton.getAttribute('aria-busy') === 'true'));
        // Homepage forms use the server-rendered variant price; product pages can change variants.
        if (price) {
          dock.querySelector('[data-dock-price]').innerHTML = price.innerHTML;
          document.querySelectorAll('[data-redd-secondary-price]').forEach((mirror) => {
            if (mirror.dataset.reddSecondaryPrice === dock.dataset.formId) mirror.innerHTML = price.innerHTML;
          });
        }
        document.querySelectorAll('[data-redd-secondary-submit]').forEach((mirror) => {
          if (mirror.getAttribute('form') !== dock.dataset.formId) return;
          mirror.disabled = mainButton.disabled;
          mirror.textContent = mainButton.textContent;
          mirror.setAttribute('aria-busy', String(mainButton.getAttribute('aria-busy') === 'true'));
        });
      };
      const observer = new MutationObserver(sync);
      observer.observe(mainButton, {attributes: true, childList: true, subtree: true});
      if (price) observer.observe(price, {childList: true, subtree: true});
      let queued = false;
      const visibility = () => {
        queued = false;
        const header = document.querySelector('.redd-header');
        const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const viewportBottom = innerHeight - (document.getElementById('PBarNextFrame') ? 64 : 0);
        const closingButtonVisible = Array.from(closingButtons).some((mirror) => {
          const rect = mirror.getBoundingClientRect();
          return rect.height > 0 && rect.top >= top && rect.bottom <= viewportBottom;
        });
        dock.hidden = mainButton.getBoundingClientRect().bottom > top || closingButtonVisible;
      };
      const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(visibility); } };
      window.addEventListener('scroll', onScroll, {passive: true});
      window.addEventListener('resize', onScroll, {passive: true});
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(dock)) return;
        observer.disconnect();
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      });
      sync();
      visibility();
    });
    // Keep bubbles beside their marker, inside the space between the sticky bars.
    const designTooltipPosition = (anchor, size, bounds, markers) => {
      const gap = 12;
      const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
      const candidates = [
        {side: 'bottom', x: anchor.left + anchor.width / 2 - size.width / 2, y: anchor.bottom + gap},
        {side: 'top', x: anchor.left + anchor.width / 2 - size.width / 2, y: anchor.top - size.height - gap},
        {side: 'left', x: anchor.left - size.width - gap, y: anchor.top + anchor.height / 2 - size.height / 2},
        {side: 'right', x: anchor.right + gap, y: anchor.top + anchor.height / 2 - size.height / 2},
        {side: 'bottom', x: anchor.left + anchor.width / 2 - size.width / 2, y: Math.max(...markers.map(marker => marker.bottom)) + gap},
        {side: 'top', x: anchor.left + anchor.width / 2 - size.width / 2, y: Math.min(...markers.map(marker => marker.top)) - size.height - gap},
      ];
      const overlap = (a, b) => Math.max(0, Math.min(a.right, b.right + 6) - Math.max(a.left, b.left - 6)) * Math.max(0, Math.min(a.bottom, b.bottom + 6) - Math.max(a.top, b.top - 6));
      return candidates.map((candidate, preference) => {
        const x = clamp(candidate.x, bounds.left, bounds.right - size.width);
        const y = clamp(candidate.y, bounds.top, bounds.bottom - size.height);
        const rect = {left: x, right: x + size.width, top: y, bottom: y + size.height};
        const hitsAnchor = overlap(rect, anchor);
        const hitsMarkers = markers.reduce((total, marker) => total + overlap(rect, marker), 0);
        const moved = Math.abs(x - candidate.x) + Math.abs(y - candidate.y);
        const horizontal = candidate.side === 'top' || candidate.side === 'bottom';
        const arrow = clamp(horizontal ? anchor.left + anchor.width / 2 - x - 4 : anchor.top + anchor.height / 2 - y - 4, 12, (horizontal ? size.width : size.height) - 20);
        return {...candidate, x, y, arrow, score: hitsAnchor * 1000 + hitsMarkers * 10 + moved + preference};
      }).sort((a, b) => a.score - b.score)[0];
    };
    root.querySelectorAll('[data-redd-design]').forEach((section) => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const items = Array.from(section.querySelectorAll('[data-design-detail]')).map(detail => ({detail, point: detail.querySelector('summary'), tip: detail.querySelector('[role="tooltip"]')}));
      const events = new AbortController();
      const options = {signal: events.signal};
      let active, pinned = false, dismissTimer, frame;
      const close = () => {
        clearTimeout(dismissTimer);
        if (!active) return;
        active.detail.open = false;
        active.point.setAttribute('aria-expanded', 'false');
        active.tip.hidden = true;
        active = null;
        pinned = false;
      };
      const position = () => {
        if (!active) return;
        const viewport = window.visualViewport;
        const bounds = {left: (viewport?.offsetLeft || 0) + 12, top: (viewport?.offsetTop || 0) + 12, right: (viewport?.offsetLeft || 0) + (viewport?.width || window.innerWidth) - 12, bottom: (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight) - 12};
        for (const selector of ['.redd-header', '.redd-product-jump']) {
          const rect = document.querySelector(selector)?.getBoundingClientRect();
          if (rect?.height && rect.top <= bounds.top + 80 && rect.bottom > bounds.top) bounds.top = rect.bottom + 12;
        }
        for (const selector of ['[data-redd-purchase-dock]', '#PBarNextFrame']) {
          const element = document.querySelector(selector);
          const rect = element?.getBoundingClientRect();
          if (element && !element.hidden && rect.height && rect.top > bounds.top) bounds.bottom = Math.min(bounds.bottom, rect.top - 12);
        }
        const anchor = active.point.getBoundingClientRect();
        if (anchor.bottom <= bounds.top || anchor.top >= bounds.bottom) { close(); return; }
        active.tip.style.maxHeight = `${Math.max(60, bounds.bottom - bounds.top)}px`;
        active.tip.style.overflowY = 'auto';
        const size = active.tip.getBoundingClientRect();
        const placement = designTooltipPosition(anchor, size, bounds, items.map(item => item.point.getBoundingClientRect()));
        active.tip.style.left = `${placement.x}px`;
        active.tip.style.top = `${placement.y}px`;
        active.tip.style.setProperty('--arrow', `${placement.arrow}px`);
        active.tip.dataset.side = placement.side;
        // Keep the pointer visible unless a very short viewport requires scrolling the text.
        active.tip.style.overflowY = active.tip.scrollHeight > active.tip.clientHeight ? 'auto' : 'visible';
      };
      const queuePosition = () => {
        if (!active || frame) return;
        frame = requestAnimationFrame(() => { frame = null; position(); });
      };
      const show = (item, pin = false) => {
        clearTimeout(dismissTimer);
        if (active !== item) close();
        active = item;
        pinned = pinned || pin;
        item.detail.open = true;
        item.point.setAttribute('aria-expanded', 'true');
        item.tip.hidden = false;
        position();
      };
      const scheduleClose = () => {
        if (!pinned) dismissTimer = setTimeout(close, 180);
      };
      items.forEach((item) => {
        item.tip.hidden = true;
        item.tip.dataset.floating = '';
        item.point.setAttribute('aria-expanded', 'false');
        // A body-level bubble cannot be clipped by the image panel or its stacking context.
        document.body.append(item.tip);
        item.point.addEventListener('click', (event) => {
          event.preventDefault();
          if (active === item && pinned) close();
          else show(item, true);
        }, options);
        item.point.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') show(item); }, options);
        item.point.addEventListener('pointerleave', scheduleClose, options);
        item.point.addEventListener('focus', () => { if (item.point.matches(':focus-visible')) show(item); }, options);
        item.point.addEventListener('blur', () => { if (active === item) close(); }, options);
        item.tip.addEventListener('pointerenter', () => clearTimeout(dismissTimer), options);
        item.tip.addEventListener('pointerleave', scheduleClose, options);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !active) return;
        event.preventDefault();
        close();
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!items.some(item => item.point.contains(event.target) || item.tip.contains(event.target))) close();
      }, options);
      window.addEventListener('scroll', queuePosition, {...options, passive: true});
      window.addEventListener('resize', queuePosition, options);
      window.visualViewport?.addEventListener('resize', queuePosition, options);
      window.visualViewport?.addEventListener('scroll', queuePosition, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(section)) return;
        close();
        cancelAnimationFrame(frame);
        events.abort();
        items.forEach(item => item.tip.remove());
      }, options);
    });
    root.querySelectorAll('[data-redd-colour-preview]').forEach((preview) => {
      if (preview.dataset.ready) return;
      preview.dataset.ready = 'true';
      preview.querySelectorAll('[data-redd-image]').forEach((button) => {
        button.addEventListener('click', () => {
          const image = preview.querySelector('[data-redd-mask-image]');
          image.src = button.dataset.reddImage;
          image.alt = button.dataset.reddAlt;
          preview.querySelectorAll('[data-redd-image]').forEach((option) => {
            const active = option === button;
            option.classList.toggle('is-active', active);
            option.setAttribute('aria-pressed', String(active));
          });
        });
      });
    });
    root.querySelectorAll('[data-redd-product-form]').forEach((form) => {
      if (form.dataset.ready) return;
      form.dataset.ready = 'true';
      const select = form.querySelector('select[name="id"]');
      if (!select) return;
      select.addEventListener('change', () => {
        const selected = select.selectedOptions[0];
        document.querySelectorAll('[data-redd-stock]').forEach((status) => {
          if (status.dataset.reddStock !== form.getAttribute('id')) return;
          const inStock = selected.dataset.inStock === 'true';
          status.dataset.inStock = String(inStock);
          status.querySelector('[data-stock-text]').textContent = inStock ? status.dataset.stockLabel : status.dataset.outOfStockLabel;
        });
        const button = form.querySelector('[type="submit"]');
        const price = form.closest('.redd-product__details').querySelector('[data-redd-price]');
        button.disabled = selected.dataset.available !== 'true';
        button.textContent = button.disabled ? form.dataset.soldOut : form.dataset.addLabel;
        price.querySelector('[data-redd-current-price]').textContent = selected.dataset.price;
        const comparePrice = price.querySelector('[data-redd-compare-price]');
        comparePrice.textContent = selected.dataset.comparePrice || '';
        comparePrice.hidden = !selected.dataset.comparePrice;
        form.closest('.redd-product__details').querySelectorAll('[data-klarna-variant]').forEach((plan) => {
          plan.hidden = plan.dataset.klarnaVariant !== selected.value;
        });
        const quantity = form.querySelector('[name="quantity"]');
        quantity.min = selected.dataset.min || '1';
        quantity.step = selected.dataset.step || '1';
        quantity.value = quantity.min;
        if (selected.dataset.max) quantity.max = selected.dataset.max;
        else quantity.removeAttribute('max');
        quantity.dispatchEvent(new Event('change', {bubbles: true}));
        const hero = form.closest('.redd-product').querySelector('[data-redd-product-hero]');
        if (hero?.dataset.studioBlack && /black|white/.test(selected.dataset.finish || '')) {
          const white = selected.dataset.finish.includes('white');
          hero.src = white ? hero.dataset.studioWhite : hero.dataset.studioBlack;
          hero.alt = white ? hero.dataset.altWhite : hero.dataset.altBlack;
          hero.removeAttribute('srcset');
        } else if (hero && selected.dataset.image) {
          hero.src = selected.dataset.image;
          hero.removeAttribute('srcset');
          hero.alt = selected.dataset.imageAlt || hero.alt;
        }
        document.querySelectorAll('[data-redd-product-secondary]').forEach((image) => {
          if (hero && image.dataset.reddProductSecondary === form.getAttribute('id')) {
            image.src = hero.src;
            image.alt = hero.alt;
          }
        });
        hero?.closest('[data-redd-media]')?.dispatchEvent(new CustomEvent('redd:media-variant'));
        const url = new URL(window.location.href);
        url.searchParams.set('variant', selected.value);
        window.history.replaceState({}, '', url);
      });
    });
    root.querySelectorAll('[data-redd-wave]').forEach((explorer) => {
      if (explorer.dataset.ready) return;
      explorer.dataset.ready = 'true';
      const buttons = Array.from(explorer.querySelectorAll('[data-wave-mode]'));
      const mobile = matchMedia('(max-width: 749px)');
      let selectedMode = 'red';
      const select = (mode) => {
        selectedMode = mode;
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.waveMode === mode)));
        explorer.querySelectorAll('[data-wave-controls]').forEach((controls) => { controls.hidden = mobile.matches; });
        explorer.querySelectorAll('[data-wave-panel]').forEach((panel) => { panel.hidden = !mobile.matches && panel.dataset.wavePanel !== mode; });
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => select(button.dataset.waveMode));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
          buttons[next].focus();
          select(buttons[next].dataset.waveMode);
        });
      });
      mobile.addEventListener('change', () => select(selectedMode));
      select(selectedMode);
    });
    root.querySelectorAll('[data-redd-session]').forEach((section) => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      section.addEventListener('change', (event) => {
        if (!event.target.matches('input[type="radio"]')) return;
        // A new duration begins at twelve, while CSS keeps a no-script fallback.
        section.querySelectorAll('.redd-session__hand, .redd-session__progress').forEach((part) => {
          part.getAnimations?.().forEach((animation) => { animation.currentTime = 0; });
        });
      });
    });
    root.querySelectorAll('[data-redd-skin]').forEach((section) => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const buttons = Array.from(section.querySelectorAll('[data-skin-mode]'));
      const select = (mode) => {
        section.dataset.light = mode;
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.skinMode === mode)));
        section.querySelectorAll('[data-skin-beam]').forEach((beam) => beam.classList.toggle('is-selected', beam.dataset.skinBeam === mode));
        section.querySelectorAll('[data-skin-description]').forEach((copy) => { copy.hidden = copy.dataset.skinDescription !== mode; });
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => select(button.dataset.skinMode));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
          buttons[next].focus();
          select(buttons[next].dataset.skinMode);
        });
      });
      section.querySelector('[data-skin-controls]').hidden = false;
    });
    root.querySelectorAll('[data-redd-help]').forEach((help) => {
      if (help.dataset.ready) return;
      help.dataset.ready = 'true';
      const search = help.querySelector('[data-help-search]');
      const input = help.querySelector('[data-help-query]');
      const clear = help.querySelector('[data-help-clear]');
      const groups = Array.from(help.querySelectorAll('[data-help-group]'));
      const normalize = (text) => text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const entries = Array.from(help.querySelectorAll('[data-help-entry]')).map((element) => ({element, text: normalize(element.textContent + ' ' + Array.from(element.querySelectorAll('img[alt]')).map((image) => image.alt).join(' '))}));
      const update = () => {
        const words = normalize(input.value).trim().split(/\s+/).filter(Boolean);
        let count = 0;
        entries.forEach(({element, text}) => {
          element.hidden = !words.every((word) => text.includes(word));
          if (!element.hidden) count += 1;
        });
        groups.forEach((group) => { group.hidden = !Array.from(group.querySelectorAll('[data-help-entry]')).some((entry) => !entry.hidden); });
        help.querySelector('[data-help-count]').textContent = help.dataset.resultsLabel.replace('__COUNT__', String(count));
        help.querySelector('[data-help-empty]').hidden = count > 0;
        help.querySelector('[data-help-categories]').hidden = words.length > 0;
        clear.hidden = input.value.length === 0;
      };
      search.addEventListener('submit', (event) => event.preventDefault());
      input.addEventListener('input', update);
      clear.addEventListener('click', () => { input.value = ''; update(); input.focus(); });
      search.hidden = false;
      update();
    });
    root.querySelectorAll('[data-redd-contact]').forEach((contact) => {
      if (contact.dataset.ready) return;
      contact.dataset.ready = 'true';
      const select = contact.querySelector('[data-contact-select]');
      const hint = contact.querySelector('[data-contact-hint]');
      const update = () => { hint.textContent = select.selectedOptions[0]?.dataset.hint || ''; };
      const setTopic = (topic) => {
        if (!Array.from(select.options).some((option) => option.value === topic)) return false;
        select.value = topic;
        update();
        return true;
      };
      select.addEventListener('change', update);
      contact.querySelectorAll('[data-contact-topic]').forEach((link) => link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (setTopic(link.dataset.contactTopic)) select.focus({preventScroll: true});
      }));
      const topic = new URLSearchParams(location.search).get('topic');
      if (topic && !contact.querySelector('[role="alert"], [role="status"]')) setTopic(topic);
      update();
    });
    root.querySelectorAll('[data-redd-reveal]').forEach((element) => {
      if (element.dataset.revealReady || !('IntersectionObserver' in window)) return;
      element.dataset.revealReady = 'true';
      element.classList.add('redd-reveal-ready');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            element.classList.add('is-visible');
            observer.disconnect();
          }
        });
      }, { threshold: 0.08 });
      observer.observe(element);
    });
  };
  initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.redd-mobile-menu[open]').forEach((menu) => {
      menu.removeAttribute('open');
      menu.querySelector('summary').focus();
    });
  });
  document.addEventListener('click', (event) => {
    const consentLink = event.target.closest('a[href="#shopifyReshowConsentBanner"]');
    if (consentLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && typeof window.privacyBanner?.showPreferences === 'function') {
      event.preventDefault();
      // Shopify injects this footer link after load. Open its native, localized
      // preferences dialog directly instead of relying on a hash-only link.
      window.privacyBanner.showPreferences().catch(() => { location.hash = 'shopifyReshowConsentBanner'; });
    }
    document.querySelectorAll('.redd-mobile-menu[open]').forEach((menu) => {
      if (!menu.contains(event.target) || event.target.closest('a')) menu.removeAttribute('open');
    });
  });
})();
